import { Alert, Box, CircularProgress, FormControlLabel, FormHelperText, Paper, Stack, Switch, Typography } from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';

function supportsBrowserNotifications() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function supportsServiceWorker() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

function getBrowserNotificationsPermissionMessage() {
  if (!supportsBrowserNotifications())
    return 'Your browser does not support notifications.';
  if (window.Notification && window.Notification.permission === 'denied')
    return 'Notification permission denied. Please enable notifications in your browser settings.';
  return null;
}

function formatActiveSubscriptions(activeSubscriptions) {
  if (activeSubscriptions <= 1)
    return '';
  return ` (and ${activeSubscriptions - 1} other device${activeSubscriptions > 2 ? 's' : ''})`;
}

function NotificationToggle({ title, helperText, checked, disabled, onChange, label }) {
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="subtitle1">{title}</Typography>
          <FormHelperText sx={{ m: 0 }}>{helperText}</FormHelperText>
        </Box>
        <FormControlLabel
          sx={{ m: 0, alignItems: 'flex-start' }}
          label={label}
          labelPlacement="start"
          control={
            <Switch
              checked={checked}
              onChange={onChange}
              disabled={disabled}
            />
          }
        />
      </Stack>
    </Paper>
  )
}

export default function ThermoAspireNotificationsForm({isAdmin = false}) {
  const [loading, setLoading] = useState(true)
  const [deviceSubscribed, setDeviceSubscribed] = useState(false)
  const [thermoAspireNotifications, setThermoAspireNotifications] = useState(false)
  const [manuscriptTrackingNotifications, setManuscriptTrackingNotifications] = useState(false)
  const [activeSubscriptions, setActiveSubscriptions] = useState(0)
  const [savingDevice, setSavingDevice] = useState(false)
  const [savingThermoAspire, setSavingThermoAspire] = useState(false)
  const [savingManuscriptTracking, setSavingManuscriptTracking] = useState(false)
  const [kronOngoingNotifications, setKronOngoingNotifications] = useState(false)
  const [savingKron, setSavingKron] = useState(false)
  const [browserNotificationsDenied, setBrowserNotificationsDenied] = useState(getBrowserNotificationsPermissionMessage())
  const [error, setError] = useState(null)

  console.log('controller', navigator.serviceWorker.controller);

  useEffect(() => {
    let cancelled = false
    const loadSettings = async () => {
      try {
        if (supportsServiceWorker()) {
          const registration = await navigator.serviceWorker.register('/serviceWorker.js')
          console.log('Service worker registered:', registration)
          const subscription = await registration.pushManager.getSubscription()
          if (!cancelled)
            setDeviceSubscribed(Boolean(subscription))
        }
        const {data} = await axios.get('/api/push/settings')
        if (!cancelled) {
          setThermoAspireNotifications(Boolean(data.thermoAspireNotifications))
          setManuscriptTrackingNotifications(Boolean(data.manuscriptTrackingNotifications))
          setKronOngoingNotifications(Boolean(data.kronOngoingNotifications))
          setActiveSubscriptions(Number(data.activeSubscriptions || 0))
          setBrowserNotificationsDenied(getBrowserNotificationsPermissionMessage())
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err)
          setError('Failed to load notification settings.')
        }
      } finally {
        if (!cancelled)
          setLoading(false)
      }
    }
    loadSettings()
    return () => {
      cancelled = true
    }
  }, [])

  const saveSettings = async (payload) => {
    const {data} = await axios.post('/api/push/settings', payload)
    setThermoAspireNotifications(Boolean(data.thermoAspireNotifications))
    setManuscriptTrackingNotifications(Boolean(data.manuscriptTrackingNotifications))
    setKronOngoingNotifications(Boolean(data.kronOngoingNotifications))
    setActiveSubscriptions(Number(data.activeSubscriptions || 0))
  }

  const ensureBrowserPermission = async () => {
    if (!supportsBrowserNotifications())
      return 'unsupported'
    if (window.Notification && window.Notification.permission === 'default')
      return window.Notification.requestPermission()
    return window.Notification && window.Notification.permission
  }

  const getCurrentSubscription = async () => {
    if (!supportsServiceWorker())
      return null
    const registration = await navigator.serviceWorker.ready
    return registration.pushManager.getSubscription()
  }

  const sendTestNotification = async () => {
    if (!supportsServiceWorker())
      return
    const registration = await navigator.serviceWorker.ready
    if (registration.active) {
      registration.active.postMessage('initiate-notifications')
      console.log('Message sent to service worker')
    }
  }

  const syncSubscriptionToServer = async (subscription) => {
    if (subscription)
      await axios.post('/api/push/subscribe', {subscription})
    else
      await axios.post('/api/push/subscribe', {})
  }

  const subscribePushManager = async () => {
    if (!supportsServiceWorker())
      return
    const registration = await navigator.serviceWorker.ready
    const existingSubscription = await registration.pushManager.getSubscription()
    if (existingSubscription === null) {
      const {data: {publicKey: vapidPublicKey}} = await axios.get('/api/push/vapid')
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: vapidPublicKey,
      })
      console.log('New subscription added.', subscription)
      await syncSubscriptionToServer(subscription)
    } else {
      console.log('Existing subscription detected.')
      await syncSubscriptionToServer(existingSubscription)
    }
  }

  const handleDeviceToggle = async () => {
    setError(null)
    try {
      if (!supportsServiceWorker())
        return
      if (deviceSubscribed) {
        const currentSubscription = await getCurrentSubscription()
        if (currentSubscription) {
          await axios.post('/api/push/unsubscribe', {subscription: currentSubscription})
          await currentSubscription.unsubscribe()
        } else {
          await axios.post('/api/push/unsubscribe', {})
        }
        setDeviceSubscribed(false)
        return
      }

      const permission = await ensureBrowserPermission()
      setBrowserNotificationsDenied(getBrowserNotificationsPermissionMessage())
      if (permission !== 'granted')
        return

      await subscribePushManager()
      setDeviceSubscribed(true)
      setBrowserNotificationsDenied(null)
      await sendTestNotification()
    } catch (err) {
      console.error(err)
      setError('Failed to update device notifications.')
    }
  }

  const handleThermoAspireToggle = async ({target: {checked}}) => {
    setError(null)
    setThermoAspireNotifications(checked)
    setSavingThermoAspire(true)
    try {
      await saveSettings({thermoAspireNotifications: checked})
    } catch (err) {
      console.error(err)
      setThermoAspireNotifications(!checked)
      setError('Failed to save Thermo Aspire notifications.')
    } finally {
      setSavingThermoAspire(false)
    }
  }

  const handleManuscriptTrackingToggle = async ({target: {checked}}) => {
    setError(null)
    setManuscriptTrackingNotifications(checked)
    setSavingManuscriptTracking(true)
    try {
      await saveSettings({manuscriptTrackingNotifications: checked})
    } catch (err) {
      console.error(err)
      setManuscriptTrackingNotifications(!checked)
      setError('Failed to save manuscript tracking notifications.')
    } finally {
      setSavingManuscriptTracking(false)
    }
  }

  const handleKronToggle = async ({target: {checked}}) => {
    setError(null)
    setKronOngoingNotifications(checked)
    setSavingKron(true)
    try {
      await saveSettings({kronOngoingNotifications: checked})
    } catch (err) {
      console.error(err)
      setKronOngoingNotifications(!checked)
      setError('Failed to save Kron ongoing-task notifications.')
    } finally {
      setSavingKron(false)
    }
  }

  const browserPermissionWarning = browserNotificationsDenied ? (
    <Alert severity="error">{browserNotificationsDenied}</Alert>
  ) : null

  return (
    <Stack spacing={2}>
      {browserPermissionWarning}
      {error && <Alert severity="error">{error}</Alert>}
      {loading && <CircularProgress size={20} />}
      <NotificationToggle
        title="This device"
        label="Enabled"
        checked={deviceSubscribed}
          disabled={loading || savingDevice || !supportsServiceWorker() || browserNotificationsDenied !== null}
        onChange={handleDeviceToggle}
          helperText={supportsServiceWorker()
          ? `This browser will ${deviceSubscribed ? 'receive' : 'not receive'} push notifications.`
          : 'Push notifications are not supported in this browser.'}
      />
      <NotificationToggle
        title="Thermo Fisher Aspire"
        label="Enabled"
        checked={thermoAspireNotifications}
        disabled={loading || savingThermoAspire}
        onChange={handleThermoAspireToggle}
        helperText={`Send branded reward alerts to subscribed devices${formatActiveSubscriptions(activeSubscriptions)}.`}
      />
      {isAdmin && <>
        <NotificationToggle
          title="Manuscript tracking"
          label="Enabled"
          checked={manuscriptTrackingNotifications}
          disabled={loading || savingManuscriptTracking}
          onChange={handleManuscriptTrackingToggle}
          helperText="Stored for future manuscript alerts. No notifications are sent yet."
        />
        <NotificationToggle
          title="Kron: ongoing task check-ins"
          label="Enabled"
          checked={kronOngoingNotifications}
          disabled={loading || savingKron}
          onChange={handleKronToggle}
          helperText={`Send a reminder if a Kron task is still running after 60 minutes${formatActiveSubscriptions(activeSubscriptions)}.`}
        />
      </>}
    </Stack>
  )
}
